import React, { useState, useEffect } from "react";
import { useSupabase } from "../SupabaseContext";
import { User, Building2, Building } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Badge } from "./ui/badge";

// Add a variant prop to control how the component is displayed
const UserProfileDisplay = ({ variant = "full" }) => {
  const supabase = useSupabase();
  const [userData, setUserData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [facilityData, setFacilityData] = useState(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        // Get the stored user data
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (!storedUser) return;

        setUserData(storedUser);

        // If we have company_id in the stored user, fetch the company details
        if (storedUser.company_id) {
          const { data: company, error: companyError } = await supabase
            .from("companies")
            .select("*")
            .eq("id", storedUser.company_id)
            .single();

          if (companyError) throw companyError;
          setCompanyData(company);
        }

        // If we have facility_id in the stored user, fetch the facility details
        if (storedUser.facility_id) {
          const { data: facility, error: facilityError } = await supabase
            .from("facilities")
            .select("*")
            .eq("id", storedUser.facility_id)
            .single();

          if (facilityError) throw facilityError;
          setFacilityData(facility);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    fetchUserDetails();
  }, [supabase]);

  if (!userData) return null;

  // Simple variant just shows initials in a colored circle
  if (variant === "initials") {
    return (
      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
        <span className="text-sm font-medium text-red-600">
          {userData.first_name?.charAt(0)}
          {userData.last_name?.charAt(0)}
        </span>
      </div>
    );
  }

  // Compact variant shows just the user name
  if (variant === "compact") {
    return (
      <div className="flex items-center bg-gray-100 px-3 py-1.5 rounded-full text-sm text-gray-700">
        <span className="mr-1.5 font-medium">
          {userData.first_name} {userData.last_name}
        </span>
      </div>
    );
  }

  // Mobile variant for sidebar
  if (variant === "mobile") {
    return (
      <div className="p-4 bg-gray-50 rounded-lg space-y-2">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-2">
            <span className="text-sm font-medium text-red-600">
              {userData.first_name?.charAt(0)}
              {userData.last_name?.charAt(0)}
            </span>
          </div>
          <span className="font-medium">
            {userData.first_name} {userData.last_name}
          </span>
        </div>

        {companyData && (
          <div className="flex items-center text-sm text-gray-600">
            <span>{companyData.name}</span>
          </div>
        )}

        <div className="flex items-center text-sm text-gray-600">
          <span>{userData.position || "Používateľ"}</span>
        </div>

        {userData.is_admin && (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            Admin
          </Badge>
        )}
        {userData.is_manager && (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            Manažér
          </Badge>
        )}
      </div>
    );
  }

  // Full variant with all details
  return (
    <div className="flex items-center space-x-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-800">
              <User className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium">
                {userData.first_name} {userData.last_name}
              </span>
            </div>
          </TooltipTrigger>
          <TooltipContent className="p-2">
            <p>
              <span className="font-semibold">Email:</span> {userData.email}
            </p>
            <p>
              <span className="font-semibold">Pozícia:</span>{" "}
              {userData.position || "Neznáma"}
            </p>
            {userData.is_admin && <Badge className="mt-1">Admin</Badge>}
            {userData.is_manager && <Badge className="mt-1">Manažér</Badge>}
          </TooltipContent>
        </Tooltip>

        {companyData && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-800">
                <Building className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">{companyData.name}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <span className="font-semibold">Kontakt:</span>{" "}
                {companyData.contact_email}
              </p>
              <p>
                <span className="font-semibold">Tel:</span>{" "}
                {companyData.contact_phone || "Neznámy"}
              </p>
              <Badge
                className={
                  companyData.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }
              >
                {companyData.is_active ? "Aktívna" : "Neaktívna"}
              </Badge>
            </TooltipContent>
          </Tooltip>
        )}

        {facilityData && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gray-100 text-gray-800">
                <Building2 className="w-4 h-4 text-gray-600" />
                <span className="text-sm font-medium">{facilityData.name}</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                <span className="font-semibold">Lokalita:</span>{" "}
                {facilityData.location || "Neznáma"}
              </p>
              <Badge
                className={
                  facilityData.is_active
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-800"
                }
              >
                {facilityData.is_active ? "Aktívna" : "Neaktívna"}
              </Badge>
            </TooltipContent>
          </Tooltip>
        )}
      </TooltipProvider>
    </div>
  );
};

export default UserProfileDisplay;
